import { useEffect, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Button, Input } from '@orion/ui';
import type { CalendlyConnectionResponse } from '@orion/contracts';
import { PageHeader } from '../../../app/shell.js';
import { ApiError } from '../../../lib/api-client.js';
import { useToast } from '../../../lib/toast.js';
import { useCallSettingsQuery, useUpdateCallSettings } from './calling-api.js';
import { calendlyConnectUrl, useCalendlyConnectionsQuery, useDisconnectCalendly, useReactivateCalendly } from './calendly-api.js';

const CONNECT_ERROR_MESSAGE: Record<string, string> = {
  not_configured: "This workspace isn't set up for Calendly yet — ask an admin to add the OAuth credentials.",
  state_mismatch: 'That connection attempt expired or was tampered with. Try connecting again.',
  connect_failed: "Something went wrong finishing the connection. If this keeps happening, try removing Orion's access from your Calendly account's connected-apps settings first, then reconnect.",
};

const STATUS_LABEL: Record<CalendlyConnectionResponse['status'], { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-success-soft text-success' },
  needs_reconnect: { label: 'Needs reconnect', className: 'bg-amber-soft text-amber-text' },
  disabled: { label: 'Disabled', className: 'bg-surface-subtle text-text-muted' },
};

function CalendlyCard({ connection }: { connection: CalendlyConnectionResponse }) {
  const disconnectMutation = useDisconnectCalendly();
  const reactivateMutation = useReactivateCalendly();
  const toast = useToast();
  const status = STATUS_LABEL[connection.status];
  const isDisabled = connection.status === 'disabled';

  const reactivate = async () => {
    try {
      await reactivateMutation.mutateAsync(connection.id);
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to reconnect.';
      toast.error(detail);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-control border border-border bg-ground p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-ink">{connection.name ?? connection.email}</span>
          <span className="text-xs text-text-muted">{connection.email}</span>
        </div>
        <span className={`flex h-6 shrink-0 items-center rounded-full px-2.5 text-xs font-semibold ${status.className}`}>{status.label}</span>
      </div>
      {connection.status === 'needs_reconnect' && (
        <p className="text-xs text-amber-text">Calendly couldn&apos;t confirm the webhook subscription — disconnect and connect again.</p>
      )}
      {connection.lastWebhookError && connection.status !== 'needs_reconnect' && <p className="text-xs text-danger">Last error: {connection.lastWebhookError}</p>}
      <p className="text-xs text-text-muted">
        {connection.lastEventAt ? `Last booking: ${new Date(connection.lastEventAt).toLocaleString('en-US')}` : 'No bookings captured yet.'}
      </p>
      {isDisabled ? (
        <Button size="sm" variant="secondary" loading={reactivateMutation.isPending} onClick={reactivate} className="self-start">
          Reconnect
        </Button>
      ) : (
        <Button size="sm" variant="secondary" loading={disconnectMutation.isPending} onClick={() => disconnectMutation.mutate(connection.id)} className="self-start">
          Disconnect
        </Button>
      )}
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function CalendlyIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.5 2.5L16 9.5" />
    </svg>
  );
}

/** Admin > Integrations > Calling & meetings — the phone number the Call
 * tab's Twilio phone-bridge rings first, the native calendar connect entry
 * points for the Meeting tab's own scheduling (still not built — see
 * CallingModule's doc comment), and Calendly: the other direction, where a
 * lead books time through a rep's existing Calendly page and the booking
 * is picked up here via webhook and logged onto their CRM record
 * automatically. */
export function CallingMeetingsPage() {
  const search = useSearch({ from: '/_app/admin/integrations/calling-meetings' }) as { connected?: string; error?: string };
  const { data: settings, isLoading } = useCallSettingsQuery();
  const updateSettings = useUpdateCallSettings();
  const { data: calendlyConnections, isLoading: calendlyLoading } = useCalendlyConnectionsQuery();
  const toast = useToast();
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (settings) setPhone(settings.bridgePhoneNumber ?? '');
  }, [settings]);

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Calling & meetings' }];
  const dirty = settings && phone.trim() !== (settings.bridgePhoneNumber ?? '');

  const save = async () => {
    try {
      await updateSettings.mutateAsync({ bridgePhoneNumber: phone.trim() });
      toast.success('Phone number saved.');
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to save.';
      toast.error(detail);
    }
  };

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="flex flex-col gap-5 px-8 py-7">
        <div className="flex flex-col gap-1">
          <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Calling & meetings</h1>
          <p className="text-sm leading-5 text-text-muted">Call leads straight from the CRM and schedule meetings from your real calendar.</p>
        </div>

        <section className="flex max-w-xl flex-col gap-3.5 rounded-dialog border border-border bg-surface p-5 shadow-card">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-control border border-border bg-surface-subtle">
              <PhoneIcon />
            </span>
            <div className="flex flex-col gap-0.5">
              <h2 className="text-[15px] font-semibold leading-5">Phone for calls</h2>
              <span className="text-[13px] text-text-muted">
                The Call tab rings this number first, then connects you to the lead — no browser microphone needed.
              </span>
            </div>
          </div>
          {!isLoading && (
            <div className="flex items-end gap-2.5 border-t border-divider pt-3.5">
              <Input
                label="Your phone number"
                placeholder="+1 416 555 0142"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="max-w-xs"
              />
              <Button size="md" onClick={save} loading={updateSettings.isPending} disabled={!dirty || !phone.trim()}>
                Save
              </Button>
            </div>
          )}
          {!isLoading && settings && !settings.callingEnabled && (
            <p className="text-xs text-text-muted">
              Placing calls isn&apos;t turned on for this workspace yet — it needs a Twilio phone number configured by an admin. Your number is
              saved now so it&apos;s ready the moment it is.
            </p>
          )}
          {!isLoading && settings?.callingEnabled && (
            <p className="text-xs text-success">Placing calls is turned on. The Call tab on a lead will ring this number first.</p>
          )}
        </section>

        <section className="flex max-w-xl flex-col gap-3.5 rounded-dialog border border-border bg-surface p-5 shadow-card">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-control border border-border bg-surface-subtle">
              <CalendarIcon />
            </span>
            <div className="flex flex-col gap-0.5">
              <h2 className="text-[15px] font-semibold leading-5">Calendar</h2>
              <span className="text-[13px] text-text-muted">
                Connect your calendar so the Meeting tab can schedule from your real availability and send the lead a native invite.
              </span>
            </div>
          </div>
          <div className="flex gap-2.5 border-t border-divider pt-3.5">
            <Button variant="secondary" size="md" disabled>
              Connect Microsoft 365
            </Button>
            <Button variant="secondary" size="md" disabled>
              Connect Google Calendar
            </Button>
          </div>
          <p className="text-xs text-text-muted">Coming soon.</p>
        </section>

        <section className="flex max-w-xl flex-col gap-3.5 rounded-dialog border border-border bg-surface p-5 shadow-card">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-control border border-border bg-surface-subtle">
              <CalendlyIcon />
            </span>
            <div className="flex flex-col gap-0.5">
              <h2 className="text-[15px] font-semibold leading-5">Calendly</h2>
              <span className="text-[13px] text-text-muted">
                When a lead books time through your Calendly page, it&apos;s logged on their CRM record automatically.
              </span>
            </div>
          </div>

          {search.error && (
            <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
              {CONNECT_ERROR_MESSAGE[search.error] ?? 'Something went wrong connecting Calendly.'}
            </div>
          )}
          {search.connected === 'calendly' && !search.error && (
            <div role="status" className="rounded-control bg-success-soft px-3 py-2 text-sm font-medium text-success">
              Calendly connected.
            </div>
          )}

          <div className="border-t border-divider pt-3.5">
            {!calendlyLoading && (calendlyConnections ?? []).length === 0 && (
              <a
                href={calendlyConnectUrl}
                className="flex items-center justify-between rounded-control border border-border-strong px-4 py-3 text-sm font-medium text-ink hover:bg-surface-subtle"
              >
                Connect Calendly
                <span className="text-text-muted">&rarr;</span>
              </a>
            )}
            {(calendlyConnections ?? []).map((c) => (
              <CalendlyCard key={c.id} connection={c} />
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
